import React, { useEffect, useMemo, useState, useRef } from "react";
import { ReactSVG } from "react-svg";
import ChartNav from "../../../../common/ChartNav";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  updateMedication,
  updateMedicationReview,
} from "../../../../../redux/modules/clinical-services/actions";
import Medications from "../../medications/medications";
import {
  drugTypes,
  getFullLabelByValue,
  reasonOptions,
} from "../clinical-assessment/data";
import SubNavigation from "../../common/sub-navigation";
import { AdherenceTypes, CommonQuestionValueTypes, DEFAULT_MEDICATION_TYPE_THERAPY } from "./data";
import MedicationReviewCard from "./medicationReviewCard";
import { getValidStep } from "../../../text/data";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import { calculateCenteredScrollPosition } from "../../../../../helpers/functions";
import { useMonographStatuses } from "../../../../../hooks/useMonographStatuses";

const STABLE_BLANK_CARD = {
  id: -1,
  name: null,
  cardName: null,
  reasonForMedication: null,
  reasonForMedicationCode: null,
  consumption: null,
  working: null,
  safe: null,
  medicationType: DEFAULT_MEDICATION_TYPE_THERAPY,
  technique: null,
  comment: null,
  specialIntructions: null,
  interventionId: null,
  intervention: null,
  status: "blank",
  generalTime: [],
};

const MedicationsReview = () => {
  //State Variables
  const {
    medications,
    clinicalServiceObj,
    amrCurrentCard,
    amrClickedMedication,
    clinicalServiceSteps,
  } = useSelector((state) => state.clinicalService);
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id: patientId, serviceId } = useParams();
  const dispatch = useDispatch();

  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [showStartReview, setShowStartReview] = useState(true);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [updateState, setupdateState] = useState(true);
  const [isMedicationReview, setIsMedicationReview] = useState(false);
  const [markCardOptional, setMarkCardOptional] = useState([]);
  const [additionalReviewCards, setAdditionalReviewCards] = useState([]);

  const [reviewedMedicationCards, setReviewedMedicationCards] = useState([]);
  const [medCards, setMedCards] = useState([]);
  const [typedMedications, setTypedMedications] = useState([]);
  const [showCompliance, setShowCompliance] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [unsavedCards, setUnsavedCards] = useState([]);
  const [showPrescriptionReviewModal, setShowPrescriptionReviewModal] = useState(false);
  const scrollContainerRef = useRef(null);
  const scrollAnimationRef = useRef(null);
  const cardRefs = useRef([]);
  const skipIndexResetRef = useRef(false);
  const pendingCenteredScrollIndexRef = useRef(null);
  const releaseSkipIndexResetAfterScrollRef = useRef(false);
  const structuralMutationRef = useRef(false);
  const blankCardMutationLockRef = useRef(false);
  const lastAuthoritativeIndexRef = useRef(null);
  const pendingAddBlankRequestRef = useRef(null);
  const selectedMedication = medCards[selectedCardIndex] || null;
  const monographIdentifiers = useMemo(
    () => typedMedications.map((med) => med?.medicationGenCode).filter(Boolean),
    [typedMedications]
  );
  const { data: monographStatusByIdentifier } =
    useMonographStatuses(monographIdentifiers);


  //------------------UseEffect Section - Loading and Updating UI---------------//
  useEffect(() => {
    setTypedMedications(
      medications.filter((x) => x.medicationType === drugTypes.Prescription)
    );
  }, [medications]);

  useEffect(() => {
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !== undefined
    ) {
      let reasons =
        clinicalServiceObj.initialAssessment.initialAssessmentForm.preAssessment
          .reasonsForMedicationReview;
      setShowCompliance(
        reasons?.some((x) => x.includes("Initiate compliance packaging"))
      );
    }
    
    let obj =
      clinicalServiceObj.initialAssessment.initialAssessmentForm.medications;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      obj !== null &&
      obj !== undefined &&
      obj?.medications?.length > 0 &&
      typedMedications?.length > 0
    ) {
      let cards = obj.medications.map((med) => {
        const manufacturerName = med?.manufacturerName || typedMedications.find(m => m.id === med.medicationId)?.manufacturerName || "";
        const manufacturerStoredType = med?.manufacturerStoredType || typedMedications.find(m => m.id === med.medicationId)?.manufacturerStoredType || "";
        const manufacturerCode = med?.manufacturerCode || typedMedications.find(m => m.id === med.medicationId)?.manufacturerCode || "";
        if (typedMedications.map((x) => x.id).includes(med.medicationId))
          return {
            id: med.medicationId,
            name: "",
            cardName: "",
            reasonForMedication: med.reasonForMedicationName,
            reasonForMedicationCode: med.reasonsForMedicationGenCode,
            medicationType: med.medicationType,
            comment: med.comments,
            specialIntructions: med.specialIntructions,
            consumption: med.adherence,
            safe: med.safety,
            technique: med.properTechnique,
            working: med.effectiveness,
            interventionId: med?.interventionId,
            intervention: med?.intervention,
            status: med?.status,
            drugType: drugTypes.Prescription,
            generalTime: med.generalTime ? med.generalTime : [],
            manufacturerCode: manufacturerCode,
            manufacturerName: manufacturerName,
            manufacturerStoredType: manufacturerStoredType,
          };
        else
          return {
            id: med.medicationId,
            name: "",
            cardName: "",
            reasonForMedication: med.reasonForMedicationName,
            reasonForMedicationCode: med.reasonsForMedicationGenCode,
            medicationType: med.medicationType,
            comment: med.comments,
            specialIntructions: med.specialIntructions,
            consumption: med.adherence,
            safe: med.safety,
            technique: med.properTechnique,
            working: med.effectiveness,
            interventionId: med?.interventionId,
            intervention: med?.intervention,
            status: med?.status,
            drugType: drugTypes.Additional,
            generalTime: med.generalTime ? med.generalTime : [],
            manufacturerCode: manufacturerCode,
            manufacturerName: manufacturerName,
            manufacturerStoredType: manufacturerStoredType,
          };
      });

      let additionalCards = cards.filter(
        (x) => x.drugType === drugTypes.Additional
      );
      setAdditionalReviewCards(additionalCards);

      medications.forEach((med) => {
        const index = cards.findIndex((o) => o.id === med.id);
        if (index > -1) {
          cards[
            index
          ].name = `${med.medicationName} ${med.route} [${med.dosage} ${med.form} ${med.frequency}]`;
          cards[index].cardName = `${med.medicationName} ${
            med.route
          } ${getFullLabelByValue(med.frequency)}`;
        }
      });
      cards = cards.filter((x) => x.drugType === drugTypes.Prescription);

      const initialCardIndex =
        amrCurrentCard !== null ? amrCurrentCard : cards.length - 1;
      const isOnMedicationCard = selectedCardIndex < cards.length;
      if (!skipIndexResetRef.current && isOnMedicationCard) {
        setSelectedCardIndex(initialCardIndex);
      }
      setReviewedMedicationCards([...cards, ...additionalCards]);
      setMedCards(cards);
      setShowStartReview(false);
      setIsMedicationReview(true);
      setCompleteSubStep(true);
    }
    setupdateState(false);
  }, [clinicalServiceObj, typedMedications]);

  // ResizeObserver: attach when the review container appears and update overflow state
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;

    const element = scrollContainerRef.current || document.getElementById("review");
    if (!element) return;

    const updateOverflow = () => {
      setIsOverflowing(element.scrollWidth >= element.clientWidth);
    };

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(element);

    // initial check
    updateOverflow();

    return () => observer.disconnect();
  }, [showStartReview, medCards.length, typedMedications.length]);

  useEffect(() => {
    // If there are no prescription review cards left, prefer moving to Additional medications (if any) rather than showing a blank prescription card.
    if (medCards.length === 0) {
      if (additionalReviewCards && additionalReviewCards.length > 0) {
        dispatch({
          type: "SET_AMR_CLICKED_MEDICATION_LOCAL",
          payload: additionalReviewCards[0],
        });
        onNext();
        return;
      }
    }

    if (
      !skipIndexResetRef.current &&
      !structuralMutationRef.current &&
      selectedCardIndex >= medCards.length &&
      medCards.length > 0
    ) {
      setSelectedCardIndex(medCards.length - 1);
    }

    // load the drug at the end, once the whole component is ready: handle clicked medication
    if (medCards.length > 0 && amrClickedMedication !== null) {
      let drug = amrClickedMedication;
      dispatch({
        type: "RESET_AMR_CLICKED_MEDICATION_LOCAL",
      });
      selectMedication(drug);
    }
  }, [medCards]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current) {
      if (medCards.length === 0) {
        addBlankCard();
      }
      hasInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    if (
      typedMedications?.filter(
        (x) =>
          x.status === "inactive" &&
          medCards.map((c) => c.name).includes(x.displayName)
      ) &&
      medCards.length > 0
    ) {
      const inactiveMed = medCards.find((card) =>
        typedMedications
          ?.filter((x) => x.status === "inactive")
          .map((i) => i.displayName)
          .includes(card.name)
      );
      if (inactiveMed !== undefined) {
        const updatedMedCards = medCards.filter(
          (x) => x.name !== inactiveMed.name
        );
        setMedCards(updatedMedCards);
      }
    }
  }, [typedMedications]);

  //Move Back and Forward
  const onNext = () => {
    proceedToNext();
  };

  const proceedToNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );

      dispatch({
        type: "RESET_AMR_CURRENT_REVIEWCARD_LOCAL",
      });
      let updatedMedications = onSaveProgress(nextStep, nextSubStep);

      // Mark all stopped medications to inactive
      updatedMedications
        .filter(
          (y) =>
            y.adherence === AdherenceTypes.Stopped &&
            y.intervention?.override === "stopped"
        )
        .forEach((x) => {
          let medObj = typedMedications.find((m) => m.id === x.medicationId);
          if (medObj !== null && medObj !== undefined)
            updateMedicationStatus(medObj, "inactive");
        });

      dispatch({
        type: "SET_AMR_INITIAL_VALUE_REVIEWCARDS_LOCAL",
        payload: [],
      });
      dispatch({
        type: "SET_AMR_UPDATED_REVIEWCARDS_LOCAL",
        payload: [],
      });
    }
  };

  const onSaveProgress = (nextStep, nextSubStep, cards = medCards) => {
    let cardUpdatesMade = currentStep === nextStep && currentSubStep === nextSubStep;
    const updatedMedications = cards
      .filter((x) => x.id !== -1)
      .map((med) => {
        return {
          medicationId: med.id,
          reasonForMedicationName: med.reasonForMedication,
          reasonsForMedicationGenCode: med?.reasonForMedicationCode,
          medicationType: med.medicationType,
          comments: med.comment,
          specialIntructions: med.specialIntructions,
          adherence: med.consumption,
          safety: med.safe,
          properTechnique: med.technique,
          effectiveness: med.working,
          interventionId: med?.interventionId,
          intervention: med?.intervention,
          status: med?.status,
          generalTime: med?.generalTime,
          manufacturerCode: med?.manufacturerCode,
          manufacturerStoredType: med?.manufacturerStoredType,
          manufacturerName: med?.manufacturerName,
        };
      });

    const additionalCards = additionalReviewCards.map((med) => {
      return {
        medicationId: med.id,
        reasonForMedicationName: med.reasonForMedication,
        reasonsForMedicationGenCode: med?.reasonForMedicationCode,
        medicationType: med.medicationType,
        comments: med.comment,
        specialIntructions: med.specialIntructions,
        adherence: med.consumption,
        safety: med.safe,
        properTechnique: med.technique,
        effectiveness: med.working,
        interventionId: med?.interventionId,
        intervention: med?.intervention,
        status: med?.status,
        generalTime: med?.generalTime,
        manufacturerCode: med?.manufacturerCode,
        manufacturerStoredType: med?.manufacturerStoredType,
        manufacturerName: med?.manufacturerName,
      };
    });

    const updatedClinicalServiceObj = {
      medications: {
        medications: [...updatedMedications, ...additionalCards],
        additionaMedicationComplete:
          clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.medications
            ?.additionaMedicationComplete ?? false,
        profileAnalysisClicked:
          clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.medications
            ?.profileAnalysisClicked,
      },
      currentStep: nextStep,
      currentSubStep: nextSubStep,
    };
    dispatch(updateMedicationReview(serviceId, updatedClinicalServiceObj));

    return updatedMedications;
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    dispatch({
      type: "RESET_AMR_CURRENT_REVIEWCARD_LOCAL",
    });
    dispatch({
      type: "SET_AMR_INITIAL_VALUE_REVIEWCARDS_LOCAL",
      payload: [],
    });
    dispatch({
      type: "SET_AMR_UPDATED_REVIEWCARDS_LOCAL",
      payload: [],
    });
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateMedicationReview(serviceId, updatedSteps));
  };

  const updateReviewedCards = (reviewedMed) => {
    if (!reviewedMedicationCards.some((i) => i.name === reviewedMed.name)) {
      setReviewedMedicationCards([...reviewedMedicationCards, reviewedMed]);
    } else {
      setReviewedMedicationCards([
        ...reviewedMedicationCards.filter((x) => x.name !== reviewedMed.name),
        reviewedMed,
      ]);
    }
  };

  const resetState = () => {
    setMedCards([]);
    setReviewedMedicationCards([]);
    setMarkCardOptional([]);
  };

  const createDrugCard = (selectedDrug, blankCard) => {
    const reason = {
      label:
        reasonOptions.find((reason) => reason.value === selectedDrug.reason)
          ?.label || selectedDrug.reason,
      value: selectedDrug.reason,
    };

    return {
      ...blankCard,
      name: selectedDrug.displayName,
      cardName: `${selectedDrug.medicationName} ${
        selectedDrug.route
      } ${getFullLabelByValue(selectedDrug.frequency)}`,
      id: selectedDrug.id,
      reasonForMedication: reason.label,
      reasonForMedicationCode: reason.value,
      comments: selectedDrug.comments,
      specialIntructions: selectedDrug.comments,
      consumption: AdherenceTypes.AsPrescribed,
      safe: CommonQuestionValueTypes.No,
      working: CommonQuestionValueTypes.Yes,
      manufacturerCode: selectedDrug?.manufacturerCode,
      manufacturerName: selectedDrug?.manufacturerName,
      manufacturerStoredType: selectedDrug?.manufacturerStoredType,
      technique: selectedDrug.displayName?.toLowerCase().includes("nhl")
        ? CommonQuestionValueTypes.Yes
        : CommonQuestionValueTypes.NotSure,
    };
  };

  // Authoritative setter to avoid concurrent index resets until forced scroll completes.
  const setSelectedIndexAuthoritative = (index) => {
    if (
      lastAuthoritativeIndexRef.current === index &&
      pendingCenteredScrollIndexRef.current === index
    ) {
      return;
    }
    skipIndexResetRef.current = true;
    pendingCenteredScrollIndexRef.current = index;
    releaseSkipIndexResetAfterScrollRef.current = true;
    lastAuthoritativeIndexRef.current = index;
    setSelectedCardIndex((prevIndex) => (prevIndex === index ? prevIndex : index));
  };

  const releaseStructuralMutation = () => {
    requestAnimationFrame(() => {
      structuralMutationRef.current = false;
    });
  };

  const addBlankCard = (skipCondition = false) => {
    const releaseMutationLock = () => {
      requestAnimationFrame(() => {
        blankCardMutationLockRef.current = false;
      });
    };
    // Acquire in-flight mutation lock synchronously to prevent concurrent callers
    if (blankCardMutationLockRef.current) {
      const lockedBlankIndex = medCards.findIndex((card) => card.status === "blank");
      
      if (lockedBlankIndex !== -1 && selectedCardIndex !== lockedBlankIndex) {
        setSelectedIndexAuthoritative(lockedBlankIndex);
      }
      return;
    }
    blankCardMutationLockRef.current = true;

    // State-based guard: never allow two consecutive blank cards.
    const lastCard = medCards.length > 0 ? medCards[medCards.length - 1] : null;
    if (lastCard && lastCard.status === "blank") {
      const lastIdx = medCards.length - 1;
      if (selectedCardIndex !== lastIdx) setSelectedIndexAuthoritative(lastIdx);
      releaseMutationLock();
      return;
    }

    const activeMedicationCount = typedMedications.filter(
      (x) => x.status === "active"
    ).length;
    const existingBlankIndex = medCards.findIndex(
      (card) => card.status === "blank"
    );

    const shouldAdd =
      skipCondition ||
      (activeMedicationCount > medCards.length &&
        !updateState &&
        existingBlankIndex === -1);

    const nextSelectedIndex =
      existingBlankIndex !== -1 ? existingBlankIndex : medCards.length;
    if (existingBlankIndex !== -1) {
      if (selectedCardIndex !== existingBlankIndex) {
        setSelectedIndexAuthoritative(existingBlankIndex);
      }
      return;
    }

    if (!shouldAdd) {
      releaseMutationLock();
      return;
    }

    structuralMutationRef.current = true;

    setMedCards((prevCards) => {
      const blankIndexInPrev = prevCards.findIndex((card) => card.status === "blank");
      if (blankIndexInPrev !== -1) {
        requestAnimationFrame(() => {
          setSelectedIndexAuthoritative(blankIndexInPrev);
          releaseStructuralMutation();
          releaseMutationLock();
        });
        return prevCards;
      }

      const nextCards = [...prevCards, { ...STABLE_BLANK_CARD }];
      const newBlankIndex = nextCards.length - 1;
      requestAnimationFrame(() => {
        setSelectedIndexAuthoritative(newBlankIndex);
        releaseStructuralMutation();
        releaseMutationLock();
      });

      return nextCards;
    });

  };

  const selectMedication = (selectedDrug) => {
    const existingCardIndex = medCards.findIndex(
      (card) => card.name === selectedDrug.displayName
    );

    if (existingCardIndex !== -1) {
      setSelectedIndexAuthoritative(existingCardIndex);
      return;
    }

    const currentCards = [...medCards];
    const existingBlankCardIndex = currentCards.findIndex(
      (card) => card.status === "blank"
    );

    const blankCardIndex = existingBlankCardIndex !== -1 
      ? existingBlankCardIndex 
      : currentCards.length;

    const cardsWithBlank = existingBlankCardIndex === -1
      ? [...currentCards, { ...STABLE_BLANK_CARD }]
      : currentCards;

    const blankCard = cardsWithBlank[blankCardIndex];
    const selectedDrugCard = createDrugCard(selectedDrug, blankCard);

    const updatedCards = [...cardsWithBlank];
    updatedCards[blankCardIndex] = selectedDrugCard;
    structuralMutationRef.current = true;
    setMedCards(updatedCards);
    setSelectedIndexAuthoritative(blankCardIndex);
    releaseStructuralMutation();
  };

  const MoveToAdditionalMedication = (selectedDrug) => {
    if (completeSubStep && unsavedCards.length === 0) {
      dispatch({
        type: "SET_AMR_CLICKED_MEDICATION_LOCAL",
        payload: selectedDrug,
      });
      onNext();
    }
  };

  const moveToCard = (index) => {
    setSelectedCardIndex(index);
  };

  const moveNext = () => {
    if (selectedCardIndex !== medCards.length - 1) {
      setSelectedCardIndex(selectedCardIndex + 1);
    }
  };
  
  const scrollToCard = (index) => {
    const container = scrollContainerRef.current || document.getElementById("review");
    if (!container) return;
    const card = cardRefs.current[index];
    if (card) {
      const containerWidth = container.offsetWidth;
      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      const scrollPosition = cardLeft - (containerWidth / 2 - cardWidth / 2);
      container.scrollTo({ left: scrollPosition, behavior: "smooth" });
    } else {
      // fallback for additional/profile cards using existing math
      const cardSize = getCardSize();
      const finalScroll = calculateCenteredScrollPosition(
        index,
        cardSize,
        container.clientWidth,
        container.scrollWidth
      );
      container.scrollTo({ left: finalScroll, behavior: "smooth" });
    }
  };

  const lastForcedScrollLeftRef = useRef(null);
  const lastForcedScrollIndexRef = useRef(null);

  // Compute target scroll position to avoid redundant smooth scrolls.
  const computeTargetScrollLeft = (index) => {
    const container = scrollContainerRef.current || document.getElementById("review");
    if (!container) return null;
    const card = cardRefs.current[index];
    if (card) {
      const containerWidth = container.offsetWidth;
      const cardLeft = card.offsetLeft;
      const cardWidth = card.offsetWidth;
      return cardLeft - (containerWidth / 2 - cardWidth / 2);
    }
    const cardSize = getCardSize();
    return calculateCenteredScrollPosition(
      index,
      cardSize,
      container.clientWidth,
      container.scrollWidth
    );
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const shouldForceScroll =
      pendingCenteredScrollIndexRef.current === selectedCardIndex;

    if (skipIndexResetRef.current && !shouldForceScroll) return;

    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
    }

    scrollAnimationRef.current = requestAnimationFrame(() => {
      const targetLeft = computeTargetScrollLeft(selectedCardIndex);
      const container = scrollContainerRef.current || document.getElementById("review");
      if (container && targetLeft != null) {
        const currentLeft = container.scrollLeft;
        const delta = Math.abs(currentLeft - targetLeft);
        if (delta > 2 || lastForcedScrollIndexRef.current !== selectedCardIndex) {
          container.scrollTo({ left: targetLeft, behavior: "smooth" });
          lastForcedScrollLeftRef.current = targetLeft;
          lastForcedScrollIndexRef.current = selectedCardIndex;
        }
      } else {
        // fallback: still attempt the original scroll if we couldn't compute target
        scrollToCard(selectedCardIndex);
      }

      if (shouldForceScroll) {
        pendingCenteredScrollIndexRef.current = null;
      }

      if (releaseSkipIndexResetAfterScrollRef.current) {
        releaseSkipIndexResetAfterScrollRef.current = false;
        requestAnimationFrame(() => {
          skipIndexResetRef.current = false;
        });
      }
    });

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, [selectedCardIndex, medCards.length]);

  const getCardSize = () => {
    return showCompliance ? 1410 : 1050;
  };

  const handleMedicationCardMovement = (allMedicationReviewed) => {
    if (!allMedicationReviewed) {
      // State-based approach: delegate to addBlankCard which enforces no consecutive blanks.
      addBlankCard();
      setCompleteSubStep(false);
    } else {
      //if all cards are reviewed but we are updating other than last card, move to next card
      if (selectedCardIndex !== medCards.length - 1) {
        moveNext();
      }
      setCompleteSubStep(true);
    }
  };

  //if drug moved to stopped taking, we have to make it inactive
  const updateMedicationStatus = (med, status) => {
    const updatedMedicationObject = {
      ...med,
      status: status,
      // mark discontinuedDuringReview when discontinued via AMR review
      ...(status === "inactive" ? { discontinuedDuringReview: true } : {}),
    };

    let medication = {
      medications: [updatedMedicationObject],
    };
    dispatch(updateMedication(patientId, medication));

    // If a medication is discontinued during AMR, remove it from clinical-service review cards so Post-Review updates immediately.
    if (status === "inactive") {
      try {
        const obj =
          clinicalServiceObj.initialAssessment.initialAssessmentForm.medications;
        if (
          clinicalServiceObj.initialAssessment.initialAssessmentForm !==
            undefined &&
          obj !== null &&
          obj !== undefined &&
          obj?.medications?.length > 0
        ) {
          const updatedReviewCards = obj.medications.filter(
            (card) => card.medicationId !== med.id
          );
          const updatedClinicalServiceObj = {
            medications: { medications: updatedReviewCards },
            currentStep: currentStep,
            currentSubStep: currentSubStep,
          };
          dispatch(updateMedicationReview(serviceId, updatedClinicalServiceObj));
        }
      } catch (e) {
        // swallow any errors — this is best-effort to keep AMR UI in sync
      }
    }
  };

  const onContinue = (reviewedMed) => {
    dispatch({
      type: "UPDATE_AMR_CURRENT_REVIEWCARD_LOCAL",
      payload: selectedCardIndex,
    });

    updateReviewedCards(reviewedMed);
    const updatedCards = [...medCards];
    updatedCards[selectedCardIndex] = reviewedMed;
    setMedCards(updatedCards);
    onSaveProgress(currentStep, currentSubStep, updatedCards);

    // Check if this is the last card and all medications are reviewed
    const isLastCard = selectedCardIndex === medCards.length - 1;
    if (isLastCard) {
      // Check remaining medications after updating the current card
      const remainingMeds = typedMedications?.filter(
        (x) =>
          x.status === "active" &&
          !updatedCards.some((r) => r.name === x.displayName)
      );
      const allCardsReviewed = remainingMeds.length === 0;

      // Show popup when saving/updating the last card and all medications are reviewed
      if (allCardsReviewed) {
        setShowPrescriptionReviewModal(true);
      }
    }
  };

  const onSkipReview = (updatedMed) => {
    dispatch({
      type: "UPDATE_AMR_CURRENT_REVIEWCARD_LOCAL",
      payload: selectedCardIndex,
    });
    setMarkCardOptional((prev) => [...prev, updatedMed.id]);
    const updatedCards = [...medCards];
    updatedCards[selectedCardIndex] = updatedMed;
    setMedCards(updatedCards);
    updateReviewedCards(updatedMed);
    onSaveProgress(currentStep, currentSubStep, updatedCards);
  };

  const onRevertSkip = (updatedMed) => {
    if (markCardOptional.includes(updatedMed.id)) {
      // Remove the element if it exists
      setMarkCardOptional(
        markCardOptional.filter((item) => item !== updatedMed.id)
      );
    }
    setMedCards((prev) => {
      const copy = [...prev];
      copy[selectedCardIndex] = updatedMed;
      return copy;
    });
  };

  const deleteCurrentCard = (index) => {
    setReviewedMedicationCards(
      reviewedMedicationCards.filter((x) => x.name !== medCards[index].name)
    );
    const updatedCards = medCards.filter((_, cardIndex) => cardIndex !== index);
    setMedCards(updatedCards);
    onSaveProgress(currentStep, currentSubStep, updatedCards);
  };
  //------------------Functions---------------//

  return (
    <div className="flex flex-col flex-1 gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications
            selectMedication={(val) => {
              if (val.medicationType === drugTypes.Additional)
                MoveToAdditionalMedication(val);
              else selectMedication(val);
            }}
            checkLastMedication={(isLast) => {
              handleMedicationCardMovement(isLast);
            }}
            selectedMedication={selectedMedication}
            reviewedMedicationCards={reviewedMedicationCards}
            isAMR={isMedicationReview}
            showMedication={true}
            defaultExpandedSection="prescription"
          />
          <div className="flex flex-1 w-full h-[660px] overflow-hidden">
            <div className="w-full gap-5 h-full">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-2xl font-bold text-teal-600">
                  Annual Medication Review - Prescriptions
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              {showStartReview && (
                <div className="flex justify-start">
                  <div
                    className={`col-span-6 rounded-[4px] border border-gray-100  h-[597px] w-[686px]`}
                  >
                    <div className="bg-gray-20 h-[134px] px-8 py-12">
                      <h3 className="text-3xl font-semibold text-center text-teal-300">
                        Review Medications
                      </h3>
                    </div>
                    <div className="flex flex-col text-gray-800 px-8 pt-8 pb-12 justify-between">
                      <div className="flex flex-col h-[280px] gap-[20px] font-medium text-[22px]">
                        <p className="border-b-[1px] text-center border-gray-100 pb-5">
                          Select one medication at a time for review.
                        </p>
                        <p className="text-center">
                          Personal Medication Record will be available in
                          Documentation at the end of the assessment.
                        </p>
                      </div>
                      <div className="py-[14px] px-8 items-center flex flex-col">
                        <Button
                          className="w-[440px] h-[83px] !text-xl !font-bold"
                          text={"Start Medication Review"}
                          type="primary"
                          onClick={() => {
                            resetState();
                            setTimeout(() => {
                              if (typedMedications.length === 0)
                                setCompleteSubStep(true);
                              setShowStartReview(false);
                              setIsMedicationReview(true);
                            }, 200);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!showStartReview && typedMedications.length === 0 && (
                <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full">
                  No Medications Available to Review.
                </div>
              )}
              {!showStartReview && typedMedications.length > 0 && (
                <>
                  <div className="h-full w-full overflow-hidden relative">
                    <div
                      ref={scrollContainerRef}
                      className="flex h-full overflow-x-auto overflow-y-hidden"
                      id="review"
                    >
                      <div className="flex-none" />
                      {medCards.map((med, i) => (
                        <React.Fragment key={med.id !== -1 ? med.id : `blank-${i}`}>
                          {isOverflowing &&
                            i === selectedCardIndex &&
                            selectedCardIndex !== 0 && (
                              <button
                                className="flex flex-row bg-gray-20 w-[36px] h-[62px] my-auto pb-[18px] mx-10 px-[10px] text-teal-600 font-bold"
                                onClick={() => setSelectedCardIndex(selectedCardIndex - 1)}
                              >
                                <ReactSVG
                                  src="/icons/arrow-right.svg"
                                  className="w-[15px] rotate-180"
                                />
                              </button>
                            )}

                          <div ref={(el) => (cardRefs.current[i] = el)} className="flex-none w-[1050px] px-6 box-border">
                            <MedicationReviewCard
                              medication={med}
                              medications={typedMedications}
                              patientId={patientId}
                              onFocus={i === selectedCardIndex}
                              monographStatusByIdentifier={monographStatusByIdentifier}
                              saveCard={(medCard) => onContinue(medCard)}
                              skipCard={(medCard) => onSkipReview(medCard)}
                              revertSkipCard={(medCard) => onRevertSkip(medCard)}
                              deleteCard={() => deleteCurrentCard(i)}
                              moveToCard={() => moveToCard(i)}
                              updateUnsavedCard={(isUnsaved) => {
                                setUnsavedCards((prev) => {
                                  if (isUnsaved && !prev.includes(i)) {
                                    return [...prev, i];
                                  }

                                  if (!isUnsaved) {
                                    return prev.filter((x) => x !== i);
                                  }

                                  return prev;
                                });
                              }}
                              showCompliance={showCompliance}
                            />
                          </div>

                          {isOverflowing &&
                            i === selectedCardIndex &&
                            selectedCardIndex !== medCards.length - 1 && (
                              <button
                                className="flex flex-row bg-gray-20 w-[36px] h-[62px] my-auto pt-[18px] mx-10 px-[10px] text-teal-600 font-bold"
                                onClick={() => setSelectedCardIndex(selectedCardIndex + 1)}
                              >
                                <ReactSVG
                                  src="/icons/arrow-right.svg"
                                  className="w-[15px]"
                                />
                              </button>
                            )}
                        </React.Fragment>
                      ))}
                      <div className="flex-none w-[calc((100vw-1050px)/2)] min-w-[0px]" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => onPrevious()}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              (!completeSubStep || showPrescriptionReviewModal) && "opacity-40 pointer-events-none"
            }`}
            onClick={() => onNext()}
            disabled={!completeSubStep || showPrescriptionReviewModal}
          />
        </div>
      </div>
      <ConfirmationModalNew
        className="!min-w-[550px]"
        type="primary"
        title="Prescriptions Medications Reviewed"
        content={() => (
          <div className="flex flex-col gap-4">
            <p className="text-body-md text-gray-600 font-medium">
              Prescription Medications have been reviewed. Select <strong>Continue</strong> to review Additional Medications.
            </p>
          </div>
        )}
        confirmText={"Continue"}
        cancelText={"Cancel"}
        confirmAction={() => {
          setShowPrescriptionReviewModal(false);
          proceedToNext();
        }}
        cancelAction={() => {
          setShowPrescriptionReviewModal(false);
        }}
        open={showPrescriptionReviewModal}
        handleClose={() => {
          setShowPrescriptionReviewModal(false);
        }}
        onCross={() => {
          setShowPrescriptionReviewModal(false);
        }}
      />
    </div>
  );
};

export default MedicationsReview;
